import { useState, useRef, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react';
import VoiceSphere from './VoiceSphere';

type DemoState = 'idle' | 'connecting' | 'active' | 'ended';

interface Message {
    role: 'agent' | 'user';
    text: string;
}

const TRANSCRIPT: Message[] = [
    { role: 'agent', text: "Hi! I'm Luna, Lumina's AI assistant. How can I help you today?" },
    { role: 'user', text: "I'd like to book an appointment for next Tuesday." },
    { role: 'agent', text: "I'd be happy to help! Let me check availability for next Tuesday. I have openings at 10 AM and 2 PM. Which works best for you?" },
    { role: 'user', text: "2 PM works great." },
    { role: 'agent', text: "You're all set for Tuesday at 2 PM. I've sent a confirmation to your email. Is there anything else?" },
];

export default function LiveDemoWidget() {
    const [state, setState] = useState<DemoState>('idle');
    const [messages, setMessages] = useState<Message[]>([]);
    const [muted, setMuted] = useState(false);
    const [callTime, setCallTime] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const transcriptRef = useRef<HTMLDivElement>(null);

    const startCall = useCallback(() => {
        setState('connecting');
        setMessages([]);
        setCallTime(0);

        setTimeout(() => {
            setState('active');
            // Start timer
            timerRef.current = setInterval(() => setCallTime(t => t + 1), 1000);

            // Simulate transcript
            TRANSCRIPT.forEach((msg, i) => {
                setTimeout(() => {
                    setMessages(prev => [...prev, msg]);
                }, (i + 1) * 2200);
            });

            // End after transcript
            setTimeout(() => {
                setState('ended');
                if (timerRef.current) clearInterval(timerRef.current);
            }, (TRANSCRIPT.length + 1) * 2200 + 1000);
        }, 1500);
    }, []);

    const endCall = useCallback(() => {
        setState('ended');
        if (timerRef.current) clearInterval(timerRef.current);
    }, []);

    useEffect(() => {
        if (transcriptRef.current) {
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, []);

    const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    return (
        <div
            className="glass-strong"
            style={{
                width: '100%',
                maxWidth: '380px',
                borderRadius: '20px',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-semibold text-starlight" style={{ fontSize: '1rem' }}>
                            Live Demo
                        </h3>
                        <p className="text-stardust" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                            Talk to Luna — our AI agent
                        </p>
                    </div>
                    {state === 'active' && (
                        <div className="flex items-center gap-2">
                            <div
                                className="animate-pulse"
                                style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'var(--color-pulse)' }}
                            />
                            <span className="font-mono text-starlight" style={{ fontSize: '0.8rem' }}>
                                {formatTime(callTime)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Voice Sphere Area */}
            <div className="flex justify-center items-center" style={{ padding: '2rem', minHeight: '220px' }}>
                <VoiceSphere
                    size={state === 'active' ? 160 : 140}
                    intensity={state === 'active' ? 0.8 : 0.3}
                />
            </div>

            {/* Transcript */}
            {messages.length > 0 && (
                <div
                    ref={transcriptRef}
                    style={{
                        maxHeight: '160px',
                        overflowY: 'auto',
                        padding: '0 1.5rem 1rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.625rem',
                    }}
                >
                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className="animate-fade-in-up"
                            style={{
                                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                maxWidth: '85%',
                            }}
                        >
                            <div
                                style={{
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.5,
                                    background: msg.role === 'agent'
                                        ? 'rgba(139, 92, 246, 0.12)'
                                        : 'rgba(45, 212, 191, 0.1)',
                                    color: msg.role === 'agent'
                                        ? 'var(--color-nova-glow)'
                                        : 'var(--color-plasma-glow)',
                                    border: `1px solid ${msg.role === 'agent'
                                        ? 'rgba(139, 92, 246, 0.15)'
                                        : 'rgba(45, 212, 191, 0.12)'}`,
                                }}
                            >
                                {msg.text}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Status text */}
            {state === 'idle' && (
                <p className="text-center text-stardust" style={{ fontSize: '0.75rem', opacity: 0.5, padding: '0 1.5rem 1rem' }}>
                    Click the button below to start a conversation with Luna
                </p>
            )}
            {state === 'connecting' && (
                <p className="text-center text-stardust animate-pulse" style={{ fontSize: '0.75rem', padding: '0 1.5rem 1rem' }}>
                    Connecting to Luna...
                </p>
            )}
            {state === 'ended' && (
                <p className="text-center text-stardust" style={{ fontSize: '0.75rem', opacity: 0.6, padding: '0 1.5rem 1rem' }}>
                    Demo complete — call lasted {formatTime(callTime)}
                </p>
            )}

            {/* Controls */}
            <div style={{ padding: '1rem 1.5rem 1.5rem', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
                {state === 'idle' || state === 'ended' ? (
                    <button
                        onClick={startCall}
                        className="btn-primary glow-cta"
                        style={{
                            padding: '0.75rem 2rem',
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}
                    >
                        <Phone style={{ width: 16, height: 16 }} />
                        {state === 'ended' ? 'Try Again' : 'Talk to Luna'}
                    </button>
                ) : state === 'active' ? (
                    <>
                        <button
                            onClick={() => setMuted(!muted)}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: muted ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.06)',
                                border: `1px solid ${muted ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: muted ? 'var(--color-ember)' : 'var(--color-stardust)',
                                transition: 'all 200ms',
                            }}
                        >
                            {muted ? <MicOff style={{ width: 18, height: 18 }} /> : <Mic style={{ width: 18, height: 18 }} />}
                        </button>
                        <button
                            onClick={endCall}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: 'rgba(239, 68, 68, 0.2)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'var(--color-ember)',
                                transition: 'all 200ms',
                            }}
                        >
                            <PhoneOff style={{ width: 18, height: 18 }} />
                        </button>
                    </>
                ) : null}
            </div>

            {/* Latency indicator */}
            {state === 'active' && (
                <div style={{
                    padding: '0.5rem 1.5rem 0.75rem',
                    textAlign: 'center',
                    fontSize: '0.65rem',
                    color: 'var(--color-stardust)',
                    opacity: 0.4,
                }}>
                    ⚡ 340ms latency · Simulated demo
                </div>
            )}
        </div>
    );
}
